<template>
    <v-container fluid>
        <div v-if="isPc" class="back-link">
            <a @click="goBack">
                <v-icon>mdi-arrow-left</v-icon>
                {{ $t('orderDetails.back') }}
            </a>
        </div>
        <v-card class="order-detail-card">
            <v-card-title>{{ $t('orderDetails.title') }}: {{ orderData.order_number }}</v-card-title>
            <v-card-text>
                <v-list dense>
                <v-list-item>
                    <v-list-item-title>{{ $t('orderDetails.orderDate') }}: {{ orderData.order_date }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('orderDetails.status') }}: {{ $t(`orderDetails.${orderData.status}`) }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t('orderDetails.total') }}: {{ orderData.total }} USDT</v-list-item-subtitle>
            
                </v-list-item>
                </v-list>

                <v-divider></v-divider>

                <v-card-subtitle>{{ $t('orderDetails.billingAddress') }}</v-card-subtitle>
                <v-list dense>
                <v-list-item>
                        <v-list-item-title>{{ billingAddress.first_name }} {{ billingAddress.last_name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ billingAddress.address_1 }}, {{ billingAddress.address_2 }}</v-list-item-subtitle>
                        <v-list-item-subtitle>{{ billingAddress.city }}, {{ billingAddress.state }} {{ billingAddress.postcode }}</v-list-item-subtitle>
                        <v-list-item-subtitle>{{ billingAddress.country }}</v-list-item-subtitle>
                        <v-list-item-subtitle>{{ billingAddress.email }}</v-list-item-subtitle>
                        <v-list-item-subtitle>{{ billingAddress.phone }}</v-list-item-subtitle>
                </v-list-item>
                </v-list>

                <v-divider></v-divider>

                <v-card-subtitle>{{ $t('orderDetails.shippingAddress') }}</v-card-subtitle>
                <v-list dense>
                <v-list-item>
                    <v-list-item-title>{{ shippingAddress.first_name }} {{ shippingAddress.last_name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ shippingAddress.address_1 }}, {{ shippingAddress.address_2 }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ shippingAddress.city }}, {{ shippingAddress.state }} {{ shippingAddress.postcode }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ shippingAddress.country }}</v-list-item-subtitle>

                    <v-list-item-action>
                        <v-btn @click="editAddress">{{ $t('orderDetails.editAddress') }}</v-btn>
                    </v-list-item-action>
                </v-list-item>
                </v-list>

                <AddressForm 
                    :visible="isFormVisible" 
                    :address="AddressForms" 
                    @addressSaved="handleAddressSaved" 
                />
                <v-divider></v-divider>

                 <!-- Product Items -->
                <v-card-title>{{ $t('orderDetails.items') }}</v-card-title>
                <v-row dense>
                <v-col v-for="item in items" :key="item.product_id" cols="12" sm="6" md="4">
                    <v-card>
                    <v-img :src="item.image" height="200px"></v-img>
                    <v-card-title>{{ item.product_name }}</v-card-title>
                    <v-card-subtitle>{{ $t('orderDetails.quantity') }}: {{ item.quantity }}</v-card-subtitle>
                    <v-card-subtitle>{{ $t('orderDetails.unitPrice') }}: {{ item.price }} USDT</v-card-subtitle>
                    <v-card-subtitle>{{ $t('orderDetails.subtotal') }}: {{ item.subtotal }} USDT</v-card-subtitle>
                    <v-card-subtitle>{{ $t('orderDetails.total') }}: {{ item.total }} USDT</v-card-subtitle>
                    </v-card>
                </v-col>
                </v-row>


                <v-divider></v-divider>

                <v-card-title>{{ $t('orderDetails.metaData') }}</v-card-title>
                <v-list dense>
                <v-list-item>
                    <v-list-item-title>
                        {{ $t('orderDetails.transactionId') }}: 
                        <a :href="getTransactionLink(metaData.transaction_id)" target="_blank">
                        {{ formatTransactionId(metaData.transaction_id) }}
                        </a>
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ $t('orderDetails.transactionStatus') }}: {{ $t(`orderDetails.${metaData.transaction_status}`) }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t('orderDetails.transactionTime') }}: {{ new Date(metaData.transaction_time * 1000).toLocaleString() }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t('orderDetails.shippingOrderNumber') }}: {{ metaData.shipping_order_number }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t('orderDetails.shippingDeliveryCompany') }}: {{ metaData.shipping_delivery_company }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t('orderDetails.shippingDeliveryTime') }}: {{ metaData.shipping_delivery_time }}</v-list-item-subtitle>
                </v-list-item>
                </v-list>

                <v-divider></v-divider>

                <!-- Table-Style Billing Section -->
                <v-card-subtitle class="billing-title">{{ $t('orderDetails.billingReceipt') }}</v-card-subtitle>
                <v-list dense class="billing-table">
                    <v-list-item>
                            <v-row class="billing-header">
                                <v-col>{{ $t('orderDetails.items') }}</v-col>
                                <v-col>{{ $t('orderDetails.quantity') }}</v-col>
                                <v-col>{{ $t('orderDetails.unitPrice') }}</v-col>
                                <v-col>{{ $t('orderDetails.total') }}</v-col>
                            </v-row>
                    </v-list-item>
                    <v-list-item v-for="item in items" :key="item.product_id">
                            <v-row class="billing-row">
                                <v-col>{{ item.product_name }}</v-col>
                                <v-col>{{ item.quantity }}</v-col>
                                <v-col>{{ item.price }} {{ orderData.currency }}</v-col>
                                <v-col>{{ item.total }} {{ orderData.currency }}</v-col>
                            </v-row>
                    </v-list-item>
                </v-list>
                <v-divider></v-divider>
                <v-list dense>
                    <v-list-item>
                            <v-list-item-title class="billing-summary">{{ $t('orderDetails.total') }}: <span class="billing-value">{{ orderData.total }} {{ orderData.currency }}</span></v-list-item-title>
                            <v-list-item-subtitle class="billing-summary">{{ $t('orderDetails.paymentMethod') }}: <span class="billing-value">{{ orderData.payment_method_title }}</span></v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </v-card-text>
        </v-card>
    </v-container>
  </template>
  
  <script setup>
  import { ref, onMounted, getCurrentInstance } from 'vue';
  import AddressForm from '@/components/AddressForm.vue';
  import { fetchOrderDetails, updateOrderAddress } from '@/api/apiService';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  const { proxy } = getCurrentInstance(); 
  
  const route = useRoute();
  const i18n = useI18n();
  const t = i18n.t;
    const orderData = ref({
        order_number: '',
        order_date: '',
        status: '',
        total: '',
        currency: '',
    });
    const billingAddress = ref({
        first_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
        email: '',
        phone: '',
    });
    const shippingAddress = ref({
        first_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
        email: '',
        phone: '',
    });
    const AddressForms = ref({
        fullName: '',
        firstName: '',
        lastName: '',
        street: '',
        region: '',
        city: '',
        phoneNumber: '',
        postalCode: '',
        country: '',
        countrySlug: '',
        regionSlug: '',
    });
    const items = ref([
        {
            product_id: '',
            product_name: '',
            quantity: '',
            subtotal: '',
            total: '',
        }
    ]);
    const metaData = ref({
        transaction_id: '',
        transaction_status: '',
        transaction_time: '',
        shipping_order_number: '',
        shipping_delivery_company: '',
        shipping_delivery_time: '',
    });
    const isFormVisible = ref(false);
    const orderId = route.params.orderId;
    const isPc = ref(window.innerWidth > 768);

    const goBack = () => {
        window.history.back();
    }

    window.addEventListener('resize', () => {
        isPc.value = window.innerWidth > 768;
    });

    onMounted(() => {
        loadOrderDetails();
    });
    function loadOrderDetails() {
        fetchOrderDetails(orderId).then(response => {
        console.log(response, 'response');
        response.data = response.data.data;
        orderData.value = response.data.order_data;
        billingAddress.value = response.data.billing_address;
        shippingAddress.value = response.data.shipping_address;
        AddressForms.value = response.data.receive_address;
        items.value = response.data.items;
        metaData.value = response.data.meta_data;
        });
    }
    function editAddress() {
        isFormVisible.value = !isFormVisible.value;
    }
    function handleAddressSaved(updatedAddress) {
        updateOrderAddress(orderId, updatedAddress).then(response => {
            if (response.status === 200) {
                orderData.value = updatedAddress;
                isFormVisible.value = false;
                loadOrderDetails();
                proxy.$toast.success(t('orderDetails.addressUpdated'));
            }
        });
    }
    function getTransactionLink(transactionId) {
        if(proxy.$store.state.chainId === 97){
            return `https://testnet.bscscan.com/tx/${transactionId}`;
        }else if(proxy.$store.state.chainId === 56){
            return `https://bscscan.com/tx/${transactionId}`;
        }
        return `https://bscscan.com/tx/${transactionId}`;
    }
    function formatTransactionId(transactionId) {
        return transactionId.slice(0, 6)+'······'+transactionId.slice(-6);
    }
  </script>
<style scoped>
.mb-4 {
  margin-bottom: 16px;
}
.my-4 {
  margin-top: 16px;
  margin-bottom: 16px;
}
.product-card {
  transition: transform 0.2s;
  cursor: pointer;
}
.product-card:hover {
  transform: scale(1.05);
}
.product-image {
  border-radius: 8px;
}
.product-title {
  font-weight: bold;
  font-size: 1.1em;
}
.v-list-item-subtitle {
  color: #555;
}

.billing-title {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 16px;
    color: #333;
}

.billing-list {
    background-color: #f9f9f9;
    padding: 16px;
    border-radius: 8px;
}

.billing-item {
    font-size: 1.2em;
    font-weight: bold;
    margin-bottom: 8px;
    color: #000;
}

.billing-detail {
    font-size: 1em;
    margin-bottom: 4px;
    color: #555;
}

.billing-value {
    font-weight: bold;
    color: #000;
}

.billing-summary {
    font-size: 1.1em;
    margin-top: 16px;
    color: #333;
}
.back-link {
    margin-bottom: 20px;
}

.back-link a {
    color: #007bff;
    text-decoration: none;
    cursor: pointer;
}

.back-link a:hover {
    text-decoration: underline;
}
</style>